import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ChatbotService } from './services/chatbot.service';
import { ChatMessage } from './interfaces/chatbot.interface';

@Component({
  selector: 'app-chatbot-widget',
  templateUrl: './chatbot-widget.component.html',
  styleUrls: ['./chatbot-widget.component.css']
})
export class ChatbotWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  public isOpen = false;
  public isMaximized = false;
  public customWidth = 420;
  public isResizing = false;
  public inputText = '';
  public messages: ChatMessage[] = [];
  public isTyping = false;
  public processingStatus = '';
  public copiedMessageId: string | null = null;
  public hasAccess = false;

  private subscriptions: Subscription = new Subscription();
  private shouldScrollToBottom = false;

  private startX = 0;
  private startWidth = 420;
  private onMouseMoveListener?: (e: MouseEvent) => void;
  private onMouseUpListener?: () => void;

  constructor(
    private chatbotService: ChatbotService,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.hasAccess = this.chatbotService.hasChatbotAccess();
    if (!this.hasAccess) return;
    this.subscriptions.add(
      this.chatbotService.messages$.subscribe((messages) => {
        this.messages = messages;
        this.shouldScrollToBottom = true;
      })
    );

    this.subscriptions.add(
      this.chatbotService.isTyping$.subscribe((typing) => {
        this.isTyping = typing;
        if (typing) {
          this.shouldScrollToBottom = true;
        }
      })
    );

    this.subscriptions.add(
      this.chatbotService.processingStatus$.subscribe((status) => {
        this.processingStatus = status;
        if (status) {
          this.shouldScrollToBottom = true;
        }
      })
    );
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.onStopResize();
  }

  /**
   * Alterna entre vista normal y altura completa (pantalla completa vertical).
   */
  public toggleMaximize(): void {
    this.isMaximized = !this.isMaximized;
    this.shouldScrollToBottom = true;
  }

  /**
   * Inicia el arrastre para redimensionar el ancho de la ventana.
   */
  public onStartResize(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.customWidth;

    this.onMouseMoveListener = (e: MouseEvent) => this.onResizing(e);
    this.onMouseUpListener = () => this.onStopResize();

    document.addEventListener('mousemove', this.onMouseMoveListener);
    document.addEventListener('mouseup', this.onMouseUpListener);
  }

  private onResizing(event: MouseEvent): void {
    if (!this.isResizing) return;
    const deltaX = this.startX - event.clientX;
    const newWidth = this.startWidth + deltaX;
    const minWidth = 360;
    const maxWidth = Math.min(1200, window.innerWidth - 32);

    this.customWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
  }

  private onStopResize(): void {
    if (this.isResizing) {
      this.isResizing = false;
      if (this.onMouseMoveListener) {
        document.removeEventListener('mousemove', this.onMouseMoveListener);
        this.onMouseMoveListener = undefined;
      }
      if (this.onMouseUpListener) {
        document.removeEventListener('mouseup', this.onMouseUpListener);
        this.onMouseUpListener = undefined;
      }
    }
  }

  public resetWidth(): void {
    this.customWidth = 420;
  }

  /**
   * Abre o cierra la ventana flotante del chat.
   */
  public toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.shouldScrollToBottom = true;
    }
  }

  /**
   * Cierra la ventana del chat.
   */
  public closeChat(): void {
    this.isOpen = false;
  }

  /**
   * Envía el mensaje escrito por el usuario.
   */
  public onSubmit(): void {
    const text = this.inputText.trim();
    if (!text || this.isTyping) {
      return;
    }

    this.inputText = '';
    const currentRoute = this.router.url || '';
    this.chatbotService.sendMessage(text, currentRoute).subscribe();
  }

  /**
   * Ejecuta una acción sugerida con un solo clic.
   */
  public onSelectSuggestion(action: string): void {
    if (this.isTyping || !action) {
      return;
    }
    const currentRoute = this.router.url || '';
    this.chatbotService.sendMessage(action, currentRoute).subscribe();
  }

  /**
   * Limpia el historial del chat de forma segura.
   */
  public onClearChat(): void {
    this.chatbotService.clearConversation();
  }

  /**
   * Maneja el evento de pulsar Enter en el input (sin Shift).
   */
  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  /**
   * Determina si el mensaje contiene datos tabulares o numéricos que se pueden graficar.
   */
  public isGraphable(msg: ChatMessage): boolean {
    if (!msg || msg.sender !== 'bot') return false;

    if (msg.data?.metrics && msg.data.metrics.length > 0) {
      return msg.data.metrics.some((m) => this.parseNumericValue(m.value) > 0);
    }

    if (msg.data?.table && msg.data.table.rows && msg.data.table.rows.length > 0) {
      return this.getTableNumericColIndex(msg.data.table) !== -1 || this.getAnyNumericColumnIndex(msg.data.table) !== -1;
    }

    return false;
  }

  /**
   * Alterna la visualización del gráfico de barras o pastel para un mensaje específico.
   */
  public toggleChart(msg: ChatMessage, type: 'bar' | 'pie'): void {
    if (msg.activeChart === type) {
      msg.activeChart = null;
    } else {
      msg.activeChart = type;
      this.shouldScrollToBottom = true;
    }
  }

  /**
   * Extrae los elementos formateados para renderizar gráficos de barras o pastel.
   */
  public getChartItems(msg: ChatMessage): {
    label: string;
    value: number;
    displayValue: string;
    percentage: number;
    barWidthPercentage: number;
    color: string;
    dashArray: string;
    dashOffset: number;
  }[] {
    const palette = [
      '#007bff', '#28a745', '#ffc107', '#fd7e14',
      '#17a2b8', '#6610f2', '#e83e8c', '#20c997', '#6c757d'
    ];

    const rawItems: { label: string; value: number; displayValue: string }[] = [];

    if (msg.data?.metrics && msg.data.metrics.length > 0) {
      msg.data.metrics.forEach((m) => {
        const val = this.parseNumericValue(m.value);
        if (val > 0) {
          rawItems.push({
            label: m.label,
            value: val,
            displayValue: `${m.value}`
          });
        }
      });
    } else if (msg.data?.table && msg.data.table.rows && msg.data.table.rows.length > 0) {
      const table = msg.data.table;
      const detectedColIdx = this.getTableNumericColIndex(table);
      const numColIdx = detectedColIdx !== -1 ? detectedColIdx : this.getAnyNumericColumnIndex(table);

      if (numColIdx !== -1) {
        table.rows.forEach((row) => {
          const rawVal = row[numColIdx];
          const val = this.parseNumericValue(rawVal);
          const label = this.getTableRowLabel(table, row, numColIdx);
          if (val > 0) {
            rawItems.push({
              label,
              value: val,
              displayValue: String(rawVal)
            });
          }
        });
      }
    }

    const total = rawItems.reduce((acc, item) => acc + item.value, 0) || 1;
    const maxVal = Math.max(...rawItems.map((item) => item.value), 1);
    let accumulatedOffset = 0;

    return rawItems.map((item, index) => {
      const percentage = Math.round((item.value / total) * 1000) / 10;
      const barWidthPercentage = Math.round((item.value / maxVal) * 1000) / 10;
      const color = palette[index % palette.length];
      const dashArray = `${percentage} ${100 - percentage}`;
      const dashOffset = -accumulatedOffset;
      accumulatedOffset += percentage;

      return {
        ...item,
        percentage,
        barWidthPercentage,
        color,
        dashArray,
        dashOffset
      };
    });
  }

  /**
   * Obtiene la suma total formateada de los valores del gráfico.
   */
  public getChartTotal(msg: ChatMessage): string {
    const items = this.getChartItems(msg);
    const total = items.reduce((acc, item) => acc + item.value, 0);
    return total.toLocaleString('es-EC', { maximumFractionDigits: 2 });
  }

  /**
   * Descarga los datos o respuesta del mensaje en formato CSV estándar compatible con Excel.
   */
  public downloadCSV(msg: ChatMessage): void {
    if (!msg) return;

    let csvContent = '\uFEFF'; // UTF-8 BOM para compatibilidad con Excel

    if (msg.data?.table && msg.data.table.columns && msg.data.table.rows) {
      // 1. Exportar Tabla
      const headers = msg.data.table.columns.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';');
      const rows = msg.data.table.rows.map((row) =>
        row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(';')
      );
      csvContent += `${headers}\r\n${rows.join('\r\n')}`;
    } else if (msg.data?.metrics && msg.data.metrics.length > 0) {
      // 2. Exportar Métricas / KPIs
      csvContent += `"Métrica";"Valor";"Variación"\r\n`;
      const rows = msg.data.metrics.map(
        (m) => `"${m.label.replace(/"/g, '""')}";"${String(m.value).replace(/"/g, '""')}";"${(m.change || '').replace(/"/g, '""')}"`
      );
      csvContent += rows.join('\r\n');
    } else {
      // 3. Exportar Texto General
      const cleanedText = (msg.text || '')
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/\r?\n/g, ' ');
      csvContent += `"Fecha";"Emisor";"Mensaje"\r\n`;
      csvContent += `"${new Date(msg.timestamp).toLocaleString()}";"${msg.sender}";"${cleanedText.replace(/"/g, '""')}"`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `asistente_kosmos_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Copia la respuesta (texto, tabla o métricas) estructurada al portapapeles.
   */
  public copyResponse(msg: ChatMessage): void {
    if (!msg) return;

    let contentToCopy = '';

    if (msg.data?.table && msg.data.table.columns && msg.data.table.rows) {
      // 1. Formato tabla con tabuladores para pegar directo en Excel, Word o correo
      const headers = msg.data.table.columns.join('\t');
      const rows = msg.data.table.rows.map((row) =>
        row.map((cell) => String(cell ?? '')).join('\t')
      );
      contentToCopy = `${headers}\n${rows.join('\n')}`;
    } else if (msg.data?.metrics && msg.data.metrics.length > 0) {
      // 2. Formato métricas estructuradas
      contentToCopy = msg.data.metrics
        .map((m) => `${m.label}: ${m.value}${m.change ? ` (${m.change})` : ''}`)
        .join('\n');
    } else {
      // 3. Formato texto limpio sin símbolos de markdown
      contentToCopy = (msg.text || '')
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/`{1,3}/g, '')
        .trim();
    }

    if (navigator && navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(contentToCopy).then(() => {
        this.setCopiedFeedback(msg.id);
      }).catch(() => {
        this.fallbackCopyText(contentToCopy, msg.id);
      });
    } else {
      this.fallbackCopyText(contentToCopy, msg.id);
    }
  }

  /**
   * Fallback de copiado para navegadores o contextos HTTP sin API de portapapeles directa.
   */
  private fallbackCopyText(text: string, msgId: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      this.setCopiedFeedback(msgId);
    } catch (err) {
      console.warn('No se pudo copiar al portapapeles:', err);
    }
    document.body.removeChild(textArea);
  }

  /**
   * Muestra feedback visual temporal de copiado exitoso (2 segundos).
   */
  private setCopiedFeedback(msgId: string): void {
    this.copiedMessageId = msgId;
    setTimeout(() => {
      if (this.copiedMessageId === msgId) {
        this.copiedMessageId = null;
      }
    }, 2000);
  }

  /**
   * Extrae valor numérico a partir de un string con símbolos o formatos ($1,234.50 -> 1234.5).
   */
  private parseNumericValue(val: any): number {
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    if (!val) return 0;
    const cleanStr = String(val)
      .replace(/[$€¥sS\s]/g, '')
      .replace(/,/g, '')
      .replace(/%/g, '')
      .trim();
    const parsed = parseFloat(cleanStr);
    return isNaN(parsed) ? 0 : parsed;
  }

  private getAnyNumericColumnIndex(table: any): number {
    if (!table?.rows?.length) return -1;
    const columnCount = table.columns?.length || table.rows[0]?.length || 0;
    for (let colIdx = 0; colIdx < columnCount; colIdx++) {
      if (table.rows.some((row: any[]) => this.parseNumericValue(row[colIdx]) > 0)) {
        return colIdx;
      }
    }
    return -1;
  }

  /**
   * Detecta inteligentemente qué columna de la tabla representa la métrica principal a graficar,
   * descartando columnas de año, mes, ID, código o fecha.
   */
  private getTableNumericColIndex(table: any): number {
    if (!table.rows || table.rows.length === 0 || !table.columns) return -1;

    const cols = table.columns.map((c: any) =>
      String(c || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
    );

    const primaryKeywords = [
      'venta', 'neto', 'neta', 'total', 'monto', 'valor', 'importe',
      'volumen', 'cantidad', 'cant', 'saldo', 'presupuesto', 'presu',
      'cumplimiento', 'meta', 'costo', 'precio', 'ingreso', 'kilos',
      'kg', 'tn', 'toneladas', 'unidades', 'libras', 'cajas'
    ];

    const blacklistKeywords = [
      'ano', 'anio', 'year', 'mes', 'month', 'dia', 'day', 'fecha',
      'date', 'id', 'cod', 'codigo', 'code', 'nro', 'num', 'numero',
      'secuencia', 'orden', 'item', 'posicion', 'ruc', 'cedula', 'telefono'
    ];

    let bestColIdx = -1;
    let highestScore = -Infinity;

    for (let colIdx = 0; colIdx < cols.length; colIdx++) {
      const colName = cols[colIdx];
      let score = 0;

      // 1. Ponderación por nombre de encabezado
      if (primaryKeywords.some((kw) => colName.includes(kw))) {
        score += 100;
      }
      if (blacklistKeywords.some((kw) => colName.includes(kw))) {
        score -= 200;
      }

      // 2. Evaluación de las filas
      let validNumericCount = 0;
      let hasDecimalsOrFormatting = false;
      const valuesSet = new Set<number>();

      for (let r = 0; r < table.rows.length; r++) {
        const rawCell = table.rows[r][colIdx];
        const numVal = this.parseNumericValue(rawCell);
        if (numVal > 0) {
          validNumericCount++;
          valuesSet.add(numVal);
          const str = String(rawCell);
          if (str.includes('.') || str.includes(',') || str.includes('$') || str.includes('%')) {
            hasDecimalsOrFormatting = true;
          }
        }
      }

      if (validNumericCount > 0) {
        score += (validNumericCount / table.rows.length) * 30;
        if (hasDecimalsOrFormatting) score += 40;
        // Si todos los valores son idénticos (como Año: 2026, 2026, 2026), penalizar
        if (valuesSet.size === 1 && table.rows.length > 1) {
          score -= 60;
        }
      } else {
        score = -Infinity;
      }

      if (score > highestScore && score > 0) {
        highestScore = score;
        bestColIdx = colIdx;
      }
    }

    // Fallback: si ninguna columna tuvo score positivo, buscar la última columna con valores numéricos válidos
    if (bestColIdx === -1) {
      for (let colIdx = cols.length - 1; colIdx >= 0; colIdx--) {
        const isBlacklisted = blacklistKeywords.some((kw) => cols[colIdx].includes(kw));
        if (!isBlacklisted && this.parseNumericValue(table.rows[0][colIdx]) > 0) {
          return colIdx;
        }
      }
    }

    return bestColIdx;
  }

  /**
   * Genera una etiqueta representativa para la fila (ej: Mes, Agencia, Producto, etc.).
   */
  private getTableRowLabel(table: any, row: any[], valueColIdx: number): string {
    const cols = (table.columns || []).map((c: any) =>
      String(c || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
    );

    let mesIdx = -1;
    let anioIdx = -1;
    let descIdx = -1;

    for (let i = 0; i < cols.length; i++) {
      if (i === valueColIdx) continue;
      const c = cols[i];
      if (c.includes('mes') || c.includes('month')) {
        mesIdx = i;
      } else if (c.includes('ano') || c.includes('anio') || c.includes('year')) {
        anioIdx = i;
      } else if (
        c.includes('agencia') || c.includes('cliente') || c.includes('material') ||
        c.includes('producto') || c.includes('descripcion') || c.includes('nombre') ||
        c.includes('canal') || c.includes('subcanal') || c.includes('marca') ||
        c.includes('vendedor') || c.includes('region') || c.includes('zona') ||
        c.includes('ciudad') || c.includes('categoria')
      ) {
        if (descIdx === -1) descIdx = i;
      }
    }

    // 1. Si tenemos mes
    if (mesIdx !== -1) {
      const rawMes = row[mesIdx];
      const mesNum = parseInt(String(rawMes), 10);
      const mesLabel = (!isNaN(mesNum) && mesNum >= 1 && mesNum <= 12) ? this.getMonthName(mesNum) : `Mes ${rawMes}`;

      if (descIdx !== -1 && row[descIdx]) {
        return `${mesLabel} (${row[descIdx]})`;
      }
      return mesLabel;
    }

    // 2. Si tenemos columna descriptiva
    if (descIdx !== -1 && row[descIdx]) {
      return String(row[descIdx]);
    }

    // 3. Primer columna que no sea el valor ni año
    for (let i = 0; i < row.length; i++) {
      if (i !== valueColIdx && i !== anioIdx && row[i] !== undefined && row[i] !== null && String(row[i]).trim()) {
        return String(row[i]);
      }
    }

    return 'Item';
  }

  /**
   * Obtiene el nombre en español del mes a partir de su número (1..12).
   */
  private getMonthName(monthNumber: number): string {
    const months = [
      '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthNumber] || `Mes ${monthNumber}`;
  }

  /**
   * Renderiza Markdown seguro (escapando todo HTML malicioso antes de formatear).
   */
  public renderSafeMarkdown(content: string): SafeHtml {
    if (!content) return '';

    // 1. Escapado estricto de etiquetas HTML para evitar XSS
    let safe = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    // 2. Formato Negrita (**texto**)
    safe = safe.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // 3. Formato Cursiva (*texto*)
    safe = safe.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // 4. Salto de línea
    safe = safe.replace(/\n/g, '<br/>');

    return this.sanitizer.bypassSecurityTrustHtml(safe);
  }

  /**
   * Auto-scroll al final del contenedor de mensajes.
   */
  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      // Ignore scroll errors
    }
  }
}
